import React, { useCallback, useState } from 'react';
import axios from 'axios';

const SALES_REPORTS = [
  { id: 'sales-products', label: 'Products' },
  { id: 'sales-product-groups', label: 'Product groups' },
  { id: 'sales-customers', label: 'Customers' },
  { id: 'sales-users', label: 'Users' },
  { id: 'sales-item-list', label: 'Item list' },
  { id: 'sales-daily', label: 'Daily sales' },
  { id: 'sales-hourly', label: 'Hourly sales' },
  { id: 'sales-profit-margin', label: 'Profit & margin' },
  { id: 'sales-invoice-list', label: 'Invoice list' },
  { id: 'sales-stock-movement', label: 'Stock movement' },
];

const PURCHASE_REPORTS = [
  { id: 'purchase-products', label: 'Products' },
  { id: 'purchase-suppliers', label: 'Suppliers' },
];

function toCSV(rows, columns) {
  const header = columns.join(',');
  const body = rows.map((r) => columns.map((c) => (r[c] != null ? String(r[c]).replace(/"/g, '""') : '')).map((v) => `"${v}"`).join(',')).join('\n');
  return header + '\n' + body;
}

export default function ReportingManager({ authConfig, backendUrl, onUnauthorized, stores = [] }) {
  const [reportType, setReportType] = useState('sales-products');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [storeId, setStoreId] = useState('');
  const [productGroup, setProductGroup] = useState('');
  const [includeSubgroups, setIncludeSubgroups] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setReportData(null);
    try {
      const params = { from, to };
      if (storeId) params.storeId = storeId;

      if (reportType.startsWith('sales-')) {
        const res = await axios.get(`${backendUrl}/api/reports/sales`, { params, ...authConfig });
        setReportData({ type: 'sales', ...res.data });
      } else if (reportType.startsWith('purchase-')) {
        const res = await axios.get(`${backendUrl}/api/reports/documents`, { params: { ...params, type: 'purchase' }, ...authConfig });
        setReportData({ type: 'purchase', ...res.data });
      } else if (reportType === 'sales-stock-movement') {
        const res = await axios.get(`${backendUrl}/api/reports/documents`, { params, ...authConfig });
        setReportData({ type: 'documents', ...res.data });
      } else if (reportType === 'sales-products' || reportType === 'sales-product-groups' || reportType === 'sales-customers' || reportType === 'sales-daily' || reportType === 'sales-hourly') {
        const res = await axios.get(`${backendUrl}/api/reports/dashboard`, { params: { ...params, year: new Date().getFullYear() }, ...authConfig });
        setReportData({ type: 'dashboard', ...res.data });
      } else {
        const res = await axios.get(`${backendUrl}/api/reports/sales`, { params, ...authConfig });
        setReportData({ type: 'sales', ...res.data });
      }
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [reportType, from, to, storeId, backendUrl, authConfig, onUnauthorized]);

  const handlePrint = () => window.print();
  const handleExcel = () => {
    let rows = [];
    let columns = [];
    if (reportData?.type === 'sales' && reportData?.byProduct?.length) {
      columns = ['productId', 'name', 'qty', 'revenue'];
      rows = reportData.byProduct;
    } else if (reportData?.type === 'sales' && reportData?.orders?.length) {
      columns = ['_id', 'createdAt', 'totalAmount', 'customerName'];
      rows = reportData.orders.map((o) => ({ _id: o._id, createdAt: o.createdAt, totalAmount: o.totalAmount, customerName: o.customerName }));
    } else if (reportData?.type === 'documents' && reportData?.documents?.length) {
      columns = ['type', 'number', 'date', 'status'];
      rows = reportData.documents;
    }
    if (rows.length) {
      const csv = toCSV(rows, columns);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportType}-${from}-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };
  const handlePdf = () => window.print();

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
        <h2 className="text-lg font-semibold">Management • Reporting</h2>
      </div>
      <div className="flex flex-1 min-h-0">
        {/* Left: report selection */}
        <div className="w-64 border-r overflow-y-auto p-4 shrink-0">
          <p className="text-sm font-medium mb-2">Select report to view or print</p>
          <input type="search" placeholder="Search reports" className="w-full border rounded px-2 py-1.5 text-sm mb-3" />
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Sales Reports</p>
            <div className="space-y-1">
              {SALES_REPORTS.map((r) => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" name="report" value={r.id} checked={reportType === r.id} onChange={(e) => setReportType(e.target.value)} />
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Purchase Reports</p>
            <div className="space-y-1">
              {PURCHASE_REPORTS.map((r) => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" name="report" value={r.id} checked={reportType === r.id} onChange={(e) => setReportType(e.target.value)} />
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Right: filters + report output */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="border-b p-4 bg-gray-50">
            <p className="text-sm font-medium mb-3">Filter</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="block text-gray-600 mb-1">Customers & suppliers</label>
                <select className="w-full border rounded px-2 py-1.5">
                  <option>All</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">User</label>
                <select className="w-full border rounded px-2 py-1.5">
                  <option>All</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Cash register</label>
                <select className="w-full border rounded px-2 py-1.5">
                  <option>All</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Product</label>
                <select className="w-full border rounded px-2 py-1.5">
                  <option>All</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Product group</label>
                <select value={productGroup} onChange={(e) => setProductGroup(e.target.value)} className="w-full border rounded px-2 py-1.5">
                  <option value="">All</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Date range</label>
                <div className="flex gap-2">
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1.5 flex-1" />
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1.5 flex-1" />
                </div>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={includeSubgroups} onChange={(e) => setIncludeSubgroups(e.target.checked)} />
                  <span>Include subgroups</span>
                </label>
              </div>
            </div>
            {stores.length > 0 && (
              <div className="mt-2">
                <label className="block text-gray-600 mb-1">Store</label>
                <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
                  <option value="">All stores</option>
                  {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={loadReport} disabled={loading} className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-500 disabled:opacity-50">Show report</button>
              <button type="button" onClick={handlePrint} className="border px-4 py-2 rounded text-sm">Print</button>
              <button type="button" onClick={handleExcel} className="border px-4 py-2 rounded text-sm">Excel</button>
              <button type="button" onClick={handlePdf} className="border px-4 py-2 rounded text-sm">PDF</button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4">
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
            {loading && <p className="text-gray-500">Loading...</p>}
            {!loading && reportData && (
              <>
                {reportData.type === 'sales' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">Total sales: P{Number(reportData.totalSales || 0).toLocaleString()} ({reportData.orderCount || 0} orders)</p>
                    {reportData.byProduct?.length > 0 && (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-100">
                            <th className="text-left p-2">Product</th>
                            <th className="text-right p-2">Qty</th>
                            <th className="text-right p-2">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.byProduct.slice(0, 50).map((p, i) => (
                            <tr key={p.productId || i} className="border-b">
                              <td className="p-2">{p.name}</td>
                              <td className="p-2 text-right">{p.qty}</td>
                              <td className="p-2 text-right">P{Number(p.revenue || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {reportData.orders?.length > 0 && (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-100">
                            <th className="text-left p-2">Order</th>
                            <th className="text-left p-2">Date</th>
                            <th className="text-right p-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.orders.slice(0, 50).map((o) => (
                            <tr key={o._id} className="border-b">
                              <td className="p-2">{o._id}</td>
                              <td className="p-2">{o.createdAt ? new Date(o.createdAt).toLocaleString() : ''}</td>
                              <td className="p-2 text-right">P{Number(o.totalAmount || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {(!reportData.byProduct?.length && !reportData.orders?.length) && <p className="text-gray-500">No data in selected period.</p>}
                  </div>
                )}
                {reportData.type === 'documents' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">{reportData.count || 0} documents</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-100">
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Number</th>
                          <th className="text-left p-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reportData.documents || []).slice(0, 50).map((d) => (
                          <tr key={d._id} className="border-b">
                            <td className="p-2">{d.type}</td>
                            <td className="p-2">{d.number || d._id}</td>
                            <td className="p-2">{d.date ? new Date(d.date).toLocaleDateString() : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {reportData.type === 'dashboard' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">Period: {from} – {to}</p>
                    {reportData.periodic?.topProducts?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Top Products</h4>
                        <ul className="text-sm space-y-1">
                          {reportData.periodic.topProducts.map((p, i) => (
                            <li key={i}>{p.name} — P{Number(p.revenue || 0).toLocaleString()}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {reportData.periodic?.topProductGroups?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Top Product Groups</h4>
                        <ul className="text-sm space-y-1">
                          {reportData.periodic.topProductGroups.map((g, i) => (
                            <li key={i}>{g.category} — P{Number(g.revenue || 0).toLocaleString()}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {reportData.periodic?.topCustomers?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Top Customers</h4>
                        <ul className="text-sm space-y-1">
                          {reportData.periodic.topCustomers.map((c, i) => (
                            <li key={i}>{c.customerName || c.customerPhone} — P{Number(c.total || 0).toLocaleString()}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(!reportData.periodic?.topProducts?.length && !reportData.periodic?.topProductGroups?.length && !reportData.periodic?.topCustomers?.length) && <p className="text-gray-500">No data in selected period.</p>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
